import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';
import { UserCheck } from 'lucide-react';
import { Input } from '@/components/Input';

const TODAY = new Date().toISOString().split('T')[0];

const VISITORS: Array<{
    id: string; name: string; company: string | null; purpose: string; host: string;
    hostDept: string | null; expectedArrival: string; actualArrival: string | null;
    expectedDeparture: string; status: string; badge: string | null; ndaSigned: boolean; photo: boolean;
}> = [];

const statusVariant: Record<string, 'primary' | 'success' | 'default' | 'error'> = {
    'pre-registered': 'primary', 'checked-in': 'success', 'checked-out': 'default', 'no-show': 'error',
};
const statusIcon: Record<string, string> = {
    'pre-registered': '', 'checked-in': '', 'checked-out': '', 'no-show': '',
};

type VisitorStatus = 'pre-registered' | 'checked-in' | 'checked-out' | 'no-show';

const SLA_POLICIES: Array<{
    id: string; name: string; active: boolean; appliesTo: string;
    responseTime: { urgent: number; high: number; medium: number; low: number };
    resolutionTime: { urgent: number; high: number; medium: number; low: number };
    businessHours: string; compliance: number;
}> = [];

function formatMinutes(min: number): string {
    if (min < 60) return `${min}m`;
    if (min < 1440) return `${Math.floor(min / 60)}h`;
    return `${Math.floor(min / 1440)}d`;
}

export function VisitorManagement() {
    const [activeTab, setActiveTab] = useState<'today' | 'pre-register' | 'history'>('today');
    const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', purpose: '', host: '', expectedArrival: '', ndaRequired: false });

    const checkedIn = VISITORS.filter(v => v.status === 'checked-in');
    const expected = VISITORS.filter(v => v.status === 'pre-registered');
    const checkedOut = VISITORS.filter(v => v.status === 'checked-out');

    return (
        <div className="space-y-6">
            {/* Header Card */}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-card border border-border p-6 rounded-3xl shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="size-10 rounded-2xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0">
                        <UserCheck className="size-5" />
                    </div>
                    <div>
                        <h1 className="tracking-tight text-foreground font-display text-page-title">Visitor Management</h1>
                        <p className="text-xs text-muted-foreground mt-0.5">Today's Visitors · Pre-Registration · QR Check-in · NDA Tracking</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="secondary" onClick={() => toast.success('Visitor report generated!')}>Reports</Button>
                    <Button variant="primary" onClick={() => setActiveTab('pre-register')}>+ Pre-register Visitor</Button>
                </div>
            </div>

            {/* Today's stats */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Checked In', value: checkedIn.length, color: 'text-success', icon: '' },
                    { label: 'Expected Today', value: expected.length, color: 'text-primary', icon: '' },
                    { label: 'Checked Out', value: checkedOut.length, color: 'text-muted-foreground', icon: '' },
                    { label: 'Total Today', value: VISITORS.length, color: 'text-foreground', icon: '' },
                ].map((s, i) => (
                    <Card key={i}><CardContent className="p-5 flex items-center gap-4">
                        <span className="text-2xl">{s.icon}</span>
                        <div>
                            <p className="text-xs text-muted-foreground font-semibold">{s.label}</p>
                            <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                        </div>
                    </CardContent></Card>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'today', label: `Today's Visitors (${TODAY})` },
                    { id: 'pre-register', label: '+ Pre-register' },
                    { id: 'history', label: 'Visitor History' },
                ].map(t => (
                    <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
                        className={`pb-4 px-4 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                        {t.label}
                        {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                    </Button>
                ))}
            </div>

            {activeTab === 'today' && (
                <div className="space-y-6">
                    {/* Checked In */}
                    {checkedIn.length > 0 && (
                        <div>
                            <h2 className="text-muted-foreground mb-3 font-display text-card-title">Checked In ({checkedIn.length})</h2>
                            <div className="space-y-3">
                                {checkedIn.map(v => (
                                    <Card key={v.id} className="border-success/20 bg-success/5">
                                        <CardContent className="p-5">
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-4">
                                                    <div className="w-12 h-12 rounded-lg flex items-center justify-center text-xl"></div>
                                                    <div>
                                                        <div className="flex items-center gap-3 mb-1">
                                                            <p className="font-semibold text-foreground">{v.name}</p>
                                                            {v.company && <span className="text-xs text-muted-foreground">— {v.company}</span>}
                                                            <Badge variant="success">Checked In</Badge>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">Purpose: <span className="text-foreground font-semibold">{v.purpose}</span></p>
                                                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                                                            <span>In: {v.actualArrival}</span>
                                                            <span>Host: {v.host} ({v.hostDept})</span>
                                                            {v.badge && <span>Badge: {v.badge}</span>}
                                                            {v.ndaSigned && <span className="text-success">NDA Signed</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button variant="secondary" size="sm" onClick={() => toast.success(`${v.name} checked out!`)}>Check Out</Button>
                                                    <Button variant="secondary" size="sm" onClick={() => toast.success('Printing badge...')}>Badge</Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Expected */}
                    {expected.length > 0 && (
                        <div>
                            <h2 className="text-muted-foreground mb-3 font-display text-card-title">Expected ({expected.length})</h2>
                            <Card>
                                <CardContent className="p-0">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                                <th className="text-left px-6 py-3">Visitor</th>
                                                <th className="text-center px-4 py-3">Expected</th>
                                                <th className="text-left px-4 py-3">Purpose</th>
                                                <th className="text-left px-4 py-3">Host</th>
                                                <th className="text-center px-4 py-3">NDA</th>
                                                <th className="px-4 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border-12">
                                            {expected.map(v => (
                                                <tr key={v.id} className="hover:bg-card/30 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <p className="font-semibold text-foreground">{v.name}</p>
                                                        {v.company && <p className="text-xs text-muted-foreground">{v.company}</p>}
                                                    </td>
                                                    <td className="px-4 py-4 text-center font-mono font-semibold text-foreground">{v.expectedArrival}</td>
                                                    <td className="px-4 py-4 text-muted-foreground">{v.purpose}</td>
                                                    <td className="px-4 py-4 text-muted-foreground">{v.host}</td>
                                                    <td className="px-4 py-4 text-center">{v.ndaSigned ? <span className="text-success text-sm"></span> : <span className="text-muted-foreground text-sm">—</span>}</td>
                                                    <td className="px-4 py-4">
                                                        <Button variant="primary" size="sm" onClick={() => toast.success(`${v.name} checked in!`)}>Check In</Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Checked Out */}
                    {checkedOut.length > 0 && (
                        <div>
                            <h2 className="text-muted-foreground mb-3 font-display text-card-title">Checked Out ({checkedOut.length})</h2>
                            <div className="space-y-2">
                                {checkedOut.map(v => (
                                    <div key={v.id} className="flex items-center justify-between px-5 py-3 bg-card border border-border rounded-lg opacity-70">
                                        <div className="flex items-center gap-3">
                                            <span></span>
                                            <div>
                                                <p className="font-semibold text-muted-foreground">{v.name}</p>
                                                <p className="text-xs text-muted-foreground">{v.company} — {v.purpose}</p>
                                            </div>
                                        </div>
                                        <div className="text-xs text-muted-foreground">In: {v.actualArrival} · Expected out: {v.expectedDeparture}</div>
                                        <Badge variant="default">Checked Out</Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {checkedIn.length === 0 && expected.length === 0 && checkedOut.length === 0 && (
                        <div className="p-12 text-center border border-dashed border-border rounded-2xl bg-card/40 space-y-3">
                            <p className="text-sm font-semibold text-foreground">No Visitors Scheduled Today</p>
                            <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                                Pre-register guests or register on-site walk-ins with automated NDA signing and QR check-in badges.
                            </p>
                            <Button variant="primary" size="sm" onClick={() => setActiveTab('pre-register')}>
                                + Pre-register Visitor
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'pre-register' && (
                <Card>
                    <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Pre-Register a Visitor</h2></CardHeader>
                    <CardContent className="p-6 space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            {([
                                { label: 'Visitor Name *', key: 'name', type: 'text', placeholder: 'Enter full legal name...' },
                                { label: 'Company / Organization', key: 'company', type: 'text', placeholder: 'e.g. Acme Corp' },
                                { label: 'Email Address *', key: 'email', type: 'email', placeholder: 'e.g. visitor@company.com' },
                                { label: 'Phone Number', key: 'phone', type: 'tel', placeholder: 'e.g. +1 (555) 000-0000' },
                                { label: 'Host Name *', key: 'host', type: 'text', placeholder: 'Search internal host employee...' },
                                { label: 'Expected Arrival *', key: 'expectedArrival', type: 'time', placeholder: 'Select arrival time...' },
                            ] as const).map(f => (
                                <div key={f.key}>
                                    <label className="text-xs font-semibold text-muted-foreground block mb-1.5">{f.label}</label>
                                    <Input value={(form as any)[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} type={f.type} placeholder={f.placeholder}
                                        className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
                                </div>
                            ))}
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Purpose of Visit *</label>
                            <select value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })}
                                className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
                                <option value="">Select purpose...</option>
                                {['Business Meeting', 'Interview', 'Vendor Meeting', 'Client Meeting', 'Delivery', 'Conference', 'Other'].map(p => <option key={p}>{p}</option>)}
                            </select>
                        </div>
                        <div className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg">
                            <input type="checkbox" id="nda" checked={form.ndaRequired} onChange={e => setForm({ ...form, ndaRequired: e.target.checked })}
                                className="w-4 h-4 accent-primary rounded" />
                            <label htmlFor="nda" className="text-sm font-semibold text-foreground cursor-pointer">Require NDA e-signature before entry</label>
                            <span className="text-xs text-muted-foreground ml-auto">Visitor will receive NDA link via email</span>
                        </div>
                        <div className="flex gap-3">
                            <Button variant="secondary" onClick={() => setActiveTab('today')}>Cancel</Button>
                            <Button variant="primary" onClick={() => { toast.success('Visitor pre-registered! Host notified via email & SMS.'); setActiveTab('today'); }} className="flex-1">
                                Pre-register & Notify Host
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {activeTab === 'history' && (
                <Card>
                    <CardContent className="p-0">
                        {VISITORS.length > 0 ? (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                        <th className="text-left px-6 py-3">Visitor</th>
                                        <th className="text-left px-4 py-3">Company</th>
                                        <th className="text-left px-4 py-3">Host</th>
                                        <th className="text-center px-4 py-3">Arrival</th>
                                        <th className="text-center px-4 py-3">Departure</th>
                                        <th className="text-center px-4 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border-12">
                                    {VISITORS.map(v => (
                                        <tr key={v.id} className="hover:bg-card/30 transition-colors">
                                            <td className="px-6 py-3">
                                                <p className="font-semibold text-foreground">{v.name}</p>
                                                <p className="text-xs text-muted-foreground">{v.purpose}</p>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">{v.company || '—'}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{v.host}</td>
                                            <td className="px-4 py-3 text-center font-mono text-muted-foreground text-xs">{v.actualArrival || v.expectedArrival}</td>
                                            <td className="px-4 py-3 text-center font-mono text-muted-foreground text-xs">{v.expectedDeparture}</td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="text-sm mr-1">{statusIcon[v.status as VisitorStatus]}</span>
                                                <Badge variant={statusVariant[v.status as VisitorStatus]}>{v.status}</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="p-12 text-center space-y-2">
                                <p className="text-sm font-semibold text-foreground">No Visitor History Found</p>
                                <p className="text-xs text-muted-foreground">Historical check-in records and digital access logs will be archived here.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export function SLAManager() {
    const [activeTab, setActiveTab] = useState<'policies' | 'breach' | 'business-hours'>('policies');

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">SLA <span className="text-foreground">Management</span></h1>
                    <p className="text-muted-foreground mt-1">SLA Policies · Breach Analysis · Business Hours · Escalation Rules</p>
                </div>
                <Button variant="primary" onClick={() => toast.success('New SLA policy dialog...')}>+ New SLA Policy</Button>
            </div>

            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'policies', label: 'SLA Policies' },
                    { id: 'breach', label: 'Breach Analysis' },
                    { id: 'business-hours', label: 'Business Hours' },
                ].map(t => (
                    <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
                        className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                        {t.label}
                        {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                    </Button>
                ))}
            </div>

            {activeTab === 'policies' && (
                <div className="space-y-4">
                    {SLA_POLICIES.map(policy => (
                        <Card key={policy.id}>
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-foreground font-display text-section-title">{policy.name}</h3>
                                            <Badge variant={policy.active ? 'success' : 'default'}>{policy.active ? 'Active' : 'Inactive'}</Badge>
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-1">Applies to: {policy.appliesTo}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">Business Hours: {policy.businessHours}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <p className="text-xs text-muted-foreground font-semibold">Compliance</p>
                                            <p className={`text-2xl font-semibold ${policy.compliance >= 90 ? 'text-success' : policy.compliance >= 80 ? 'text-warning' : 'text-destructive'}`}>{policy.compliance}%</p>
                                        </div>
                                        <Button variant="secondary" size="sm" onClick={() => toast.success(`Editing ${policy.name}...`)}>Edit</Button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-muted-foreground font-semibold mb-2">Response Time Targets</p>
                                        <div className="space-y-1">
                                            {Object.entries(policy.responseTime).map(([priority, mins]) => (
                                                <div key={priority} className="flex justify-between text-sm">
                                                    <span className="capitalize text-muted-foreground">{priority}</span>
                                                    <span className={`font-semibold ${priority === 'urgent' ? 'text-destructive' : priority === 'high' ? 'text-warning' : 'text-foreground'}`}>{formatMinutes(mins as number)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground font-semibold mb-2">Resolution Time Targets</p>
                                        <div className="space-y-1">
                                            {Object.entries(policy.resolutionTime).map(([priority, mins]) => (
                                                <div key={priority} className="flex justify-between text-sm">
                                                    <span className="capitalize text-muted-foreground">{priority}</span>
                                                    <span className={`font-semibold ${priority === 'urgent' ? 'text-destructive' : priority === 'high' ? 'text-warning' : 'text-foreground'}`}>{formatMinutes(mins as number)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-border">
                                    <p className="text-xs text-muted-foreground font-semibold mb-2">Compliance Progress</p>
                                    <div className="h-2 bg-background rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${policy.compliance >= 90 ? 'bg-success' : policy.compliance >= 80 ? 'bg-warning' : 'bg-destructive'}`} style={{ width: `${policy.compliance}%` }} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {activeTab === 'breach' && (
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                        {[
                            { label: 'SLA Breaches Today', value: 3, color: 'text-destructive', border: 'border-destructive/20' },
                            { label: 'At Risk (next 2h)', value: 6, color: 'text-warning', border: 'border-warning/20' },
                            { label: 'On Track', value: 38, color: 'text-success', border: 'border-success/20' },
                        ].map((s, i) => (
                            <Card key={i} className={`border ${s.border}`}><CardContent className="p-5">
                                <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
                                <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                            </CardContent></Card>
                        ))}
                    </div>
                    <Card>
                        <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Breached & At-Risk Tickets</h2></CardHeader>
                        <CardContent className="p-0">
                            {[
                                { id: 'TKT-2026-00395', subject: 'Cannot login to CRM system', priority: 'urgent', assignee: 'Unassigned', breach: 'Response time breached — 45m overdue', type: 'breached' },
                                { id: 'TKT-2026-00388', subject: 'Server backup failed — 3 days running', priority: 'high', assignee: 'Mike Johnson', breach: 'Resolution due in 1h 20m', type: 'at-risk' },
                                { id: 'TKT-2026-00412', subject: 'Cannot access email – Outlook crashing', priority: 'urgent', assignee: 'Sarah Wilson', breach: 'Resolution due in 2h 15m', type: 'at-risk' },
                            ].map((t, i) => (
                                <div key={i} className={`flex items-center justify-between px-6 py-4 border-b border-border ${t.type === 'breached' ? 'bg-destructive/5' : ''}`}>
                                    <div className="flex items-center gap-4">
                                        <span className="text-xl">{t.type === 'breached' ? '' : ''}</span>
                                        <div>
                                            <p className="text-xs font-mono text-primary font-semibold">{t.id}</p>
                                            <p className="font-semibold text-foreground text-sm">{t.subject}</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">{t.assignee} · {t.breach}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Badge variant={t.priority === 'urgent' ? 'error' : 'warning'}>{t.priority}</Badge>
                                        <Button variant="primary" size="sm" onClick={() => toast.success('Escalating ticket...')}>Escalate</Button>
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === 'business-hours' && (
                <Card>
                    <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
                        <h2 className="text-foreground font-display text-section-title">Business Hours Configuration</h2>
                        <Button variant="secondary" size="sm" onClick={() => toast.success('Adding holiday...')}>+ Add Holiday</Button>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                        <div className="space-y-2">
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                                <div key={day} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-success" />
                                        <span className="font-semibold text-foreground">{day}</span>
                                    </div>
                                    <span className="text-sm text-muted-foreground font-mono">9:00 AM – 6:00 PM IST</span>
                                    <Badge variant="success">Open</Badge>
                                </div>
                            ))}
                            {['Saturday', 'Sunday'].map(day => (
                                <div key={day} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg opacity-60">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-text-600" />
                                        <span className="font-semibold text-muted-foreground">{day}</span>
                                    </div>
                                    <span className="text-sm text-muted-foreground">Closed</span>
                                    <Badge variant="default">Closed</Badge>
                                </div>
                            ))}
                        </div>
                        <div className="pt-4 border-t border-border">
                            <p className="text-xs font-semibold text-muted-foreground mb-3">Public Holidays (2026)</p>
                            <div className="grid grid-cols-2 gap-2">
                                {['Jan 26 – Republic Day', 'Mar 25 – Holi', 'Apr 14 – Dr. Ambedkar Jayanti', 'Aug 15 – Independence Day', 'Oct 2 – Gandhi Jayanti', 'Oct 24 – Dussehra', 'Nov 1 – Diwali', 'Dec 25 – Christmas'].map(h => (
                                    <div key={h} className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                                        <span></span> {h}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
